import { create } from 'zustand';
import { NPC_REGISTRY } from '../data/npcs.config';

const STORAGE_KEY = 'usagi_npc';

interface NpcState {
  /** NPC id → unlocked (persisté). Déblocage manuel via `unlock(id)` ; le seed boot ne couvre que les NPC `defaultUnlocked`. */
  unlocked: Record<string, boolean>;
}

interface NpcActions {
  unlock: (npcId: string) => void;
  isUnlocked: (npcId: string) => boolean;
  resetUnlocks: () => void;
}

function load(): NpcState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<NpcState>;
      return { unlocked: parsed.unlocked ?? {} };
    }
  } catch {
    /* ignore */
  }
  return { unlocked: {} };
}

function persist(state: NpcState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ unlocked: state.unlocked }),
  );
}

export const useNpcStore = create<NpcState & NpcActions>((set, get) => ({
  ...load(),

  unlock: (npcId) => {
    set((s) => {
      const next = { unlocked: { ...s.unlocked, [npcId]: true } };
      persist(next);
      return next;
    });
  },

  isUnlocked: (npcId) => get().unlocked[npcId] === true,

  resetUnlocks: () => {
    const reset = { unlocked: {} };
    set(reset);
    persist(reset);
  },
}));

/**
 * At boot, ensures every `defaultUnlocked: true` NPC is in the store.
 * Idempotent. Post-launch / quest-bound NPCs (`defaultUnlocked: false`) are
 * unlocked manually via `useNpcStore.unlock(id)` at their contextual trigger.
 */
export function seedDefaultNpcUnlocks(): void {
  const store = useNpcStore.getState();
  for (const def of Object.values(NPC_REGISTRY)) {
    if (def.defaultUnlocked && !store.isUnlocked(def.id)) {
      store.unlock(def.id);
    }
  }
}
